/**
 * แหล่งรายการของ "picker ตั้งค่า" (Flow builder และหน้า admin อื่น) ในโหมด App Knowledge Center
 * แผนงาน: tasks/2026-10-28-kmc-full-center-mode/TASK.md (G9 · AC-9 · A4/A13/A19 · D2/D4) · สัญญา: CONTRACT.md §1, §5
 *
 * ── วิธีใช้ในหน้า Vue (ทุกเมธอด **ไม่ throw** — พลาดแล้วได้รายการว่าง + เหตุผล)
 *   import { kccPickerSource, kccUnavailableReason } from '@/services/kccPickerSource'
 *   const list = await kccPickerSource.vectorList();
 *   if (list === null) { …เรียก $call เดิมของหน้า (โหมดปิด = พฤติกรรมเดิมทุกบรรทัด)… }
 *   else { this.DDVector = list; this.kccDocsUnavailable = kccUnavailableReason(list); } // '' = ไม่มีปัญหา
 *
 * ── ตัดสินโหมด (A19) จาก `kccApi.status()`
 *   enabled        = "ใช้แหล่งไหน" — false ⇒ ทุกเมธอดคืน `null` (หน้าใช้ของเดิม) และไม่ยิงอะไรต่อ
 *   runtimeReady   = "ดึงรายการได้ไหม" — enabled && !runtimeReady ⇒ `[]` + เหตุผล `kccCenterListUnavailable`
 *                    (ไม่ถอยไปของในเครื่อง — ตรงกับฝั่ง BE ที่โหมด Center ไม่มี key = ไม่มีข้อมูล)
 *   status พลาด/401 (role นอก 0,1,2,6) ⇒ ถือว่าปิด (ข้อจำกัดเดิม A4) · cache 60 วิ (ทั้งหน้าเห็นโหมดเดียวกัน) · `reset()` ล้าง
 *
 * ── รายการ (ทุกตัวเป็นของ Center เท่านั้น — ของในเครื่องไม่ปน)
 *   vectorList()               catalog ของ Runtime key (A13: own ∪ shared ไม่ผ่าน Policy) → ตัวเลือกรูปเดียวกับ `DDVector`
 *                              `{ id: 0, name, typeName, docType: 'Center', displayName, description, documentCategory,
 *                                 title, subTitle, itemID }`
 *   availableMetaDocuments()   catalog → รูปเดียวกับ `GetAvailableMetaDocuments` `{ vectorDbName, itemID, nameEN, nameTH,
 *                              documentCategory, title, subTitle }` · `{category, type}` กรอง `title`/`subTitle` แบบเส้นเดิม
 *   graphs()                   `kccApi.graphs()` (id ของ Center — D2) → `{ id, communityName, description, groupCount,
 *                              documentCount, isActive }` เฉพาะ isActive (เส้นเดิมก็ส่งเฉพาะ graph ที่ใช้งานได้)
 *   metaDocCategories()        ทะเบียนหัวข้อของ Center (passthrough `metadoc/masterdata/metadoc-categories`, Admin key,
 *                              role 0,1,2) → `{ title, code, displayOrder, isActive }`
 *   metaDocSubCategoriesAll(h) ทะเบียนหมวดต่อหัวข้อ (`…/metadoc-subcategories/category/{title}` ทีละหัวข้อ) →
 *                              `{ categoryTitle, key, code, displayOrder, isActive }`
 *   metaDocTaxonomy()          สองตัวบนรวมกัน → `{ headers, categories, source: 'registry'|'catalog', reason }` ·
 *                              ทะเบียนอ่านไม่ได้ (401 role 6 / 412 ไม่มี Admin key / 424 / 502 / อะไรก็ตาม) ⇒ สร้างจาก
 *                              catalog: หัวข้อ = `documentCategory`, หมวด = `title` (คู่เดียวกับที่ runtime จับ — A4)
 *   รายการที่คืนติด property แบบ non-enumerable: `unavailableReason` (ชื่อ i18n หรือไม่มี) และ `kccSource`
 *   ('catalog'|'registry'|'graphs') — อ่านผ่าน `kccUnavailableReason(list)` / `kccListSource(list)`
 *
 * ── เหตุผล (ชื่อ i18n `kcc*` — มีครบ th/en ใน plugins/i18n.js)
 *   kccCenterListUnavailable  เปิดโหมดแต่ไม่มี Runtime key (หรือ proxy ตอบ 412)
 *   kccCenterGraphsUnavailable เหมือนบนสำหรับ graph
 *   kccKeyRejected (424) · kccCenterUnreachable (502/504/network) · kccCenterError (อื่น ๆ) — จาก `kccErrorKey`
 *
 * ── ข้อห้าม
 *   🔴 ห้ามใช้ `$call` (401 ⇒ ล้าง session ไป /login) — ใช้ axios ของ `kccApi.js` (`kccHttp`) เท่านั้น
 *   🔴 ห้ามย้ายเมธอดเหล่านี้ไป `metaDocSource.js` (เทสต์ของไฟล์นั้นปักว่าเป็นของหน้า MetaDoc/DocumentControl เท่านั้น)
 *   key/header ของ Center ไม่อยู่ที่ browser — proxy ของ Genie ใส่ให้เอง
 *
 * import แบบ relative เพื่อให้ `tests/kccPickerSource.test.mjs` import ใต้ Node เปล่าได้
 */

import { kccApi as defaultApi, kccHttp, unwrap, toError, kccErrorKey } from './kccApi.js';

const ROOT = 'api/v1/KnowledgeCenterClient';
const MASTERDATA = `${ROOT}/metadoc/masterdata`;

/** path ของ passthrough ทะเบียนหัวข้อ/หมวด (ลงทะเบียน APIAuthen role 0,1,2 แล้วในรอบ 27 ต.ค.) */
export const KCC_PICKER_PATHS = Object.freeze({
  metaDocCategories: `${MASTERDATA}/metadoc-categories`,
  metaDocSubCategoriesByCategory: `${MASTERDATA}/metadoc-subcategories/category/{categoryTitle}`,
});

/** เหตุผลเมื่อเปิดโหมดแต่ดึงรายการเอกสารไม่ได้ (ไม่มี Runtime key) — ชื่อ i18n */
export const KCC_LIST_UNAVAILABLE = 'kccCenterListUnavailable';
/** เหตุผลเดียวกันสำหรับรายการ graph — ชื่อ i18n */
export const KCC_GRAPHS_UNAVAILABLE = 'kccCenterGraphsUnavailable';

/** อายุ cache ของ status (ms) — ทั้งหน้าตัดสินโหมดเดียวกัน · ค่าตั้งฝั่ง server ก็ refresh ทุก 60 วิ */
export const STATUS_TTL_MS = 60000;
/** อายุ cache ของ catalog (ms) — vectorList/availableMetaDocuments/ทะเบียนสำรองใช้ผลเดียวกัน */
export const CATALOG_TTL_MS = 30000;

const OFF = Object.freeze({ enabled: false, runtimeReady: false, adminReady: false });

// ── property non-enumerable บนรายการ ──────────────────────────────────────────────────────────

function annotate(list, { reason = '', source = '' } = {}) {
  const arr = Array.isArray(list) ? list : [];
  if (reason) Object.defineProperty(arr, 'unavailableReason', { value: reason, enumerable: false, configurable: true });
  if (source) Object.defineProperty(arr, 'kccSource', { value: source, enumerable: false, configurable: true });
  return arr;
}

/** ชื่อ i18n ของเหตุผลที่รายการว่าง ('' = ไม่มีปัญหา หรือรายการไม่ได้มาจากไฟล์นี้) */
export function kccUnavailableReason(list) {
  return (list && typeof list === 'object' && list.unavailableReason) || '';
}

/** ที่มาของรายการ: 'catalog' | 'registry' | 'graphs' | '' */
export function kccListSource(list) {
  return (list && typeof list === 'object' && list.kccSource) || '';
}

// ── ตัวแปลงรูป (pure — export ไว้ให้เทสต์/หน้าอื่นใช้) ─────────────────────────────────────────

/** อ่านช่องแบบ camelCase ก่อน แล้วค่อย PascalCase (proxy เป็น STJ camelCase — กันไว้เผื่อ) */
function pick(obj, key) {
  if (!obj || typeof obj !== 'object') return undefined;
  if (obj[key] !== undefined) return obj[key];
  return obj[key.charAt(0).toUpperCase() + key.slice(1)];
}

const text = (value) => (value === undefined || value === null ? '' : String(value).trim());

/** แถว catalog → ตัวเลือกรูป `DDVector` (item-value = `name`) · ไม่มีชื่อ ⇒ null */
export function toVectorOption(doc) {
  const name = text(pick(doc, 'vectordbName'));
  if (!name) return null;
  const nameEn = text(pick(doc, 'nameEn'));
  const nameTh = text(pick(doc, 'nameTh'));
  const title = text(pick(doc, 'title'));
  return {
    id: 0,
    name,
    typeName: nameEn,
    docType: 'Center',
    displayName: nameEn ? `${name} (${nameEn}) [Center]` : `${name} [Center]`,
    description: title || nameTh || nameEn,
    documentCategory: text(pick(doc, 'documentCategory')),
    title,
    subTitle: text(pick(doc, 'subTitle')),
    itemID: text(pick(doc, 'itemId')) || null,
  };
}

/** แถว catalog → รูป `DropdownAvailableMetaDocument` ของเส้นเดิม (`GetAvailableMetaDocuments`) · ไม่มีชื่อ ⇒ null */
export function toAvailableMetaDocument(doc) {
  const vectorDbName = text(pick(doc, 'vectordbName'));
  if (!vectorDbName) return null;
  return {
    vectorDbName,
    itemID: text(pick(doc, 'itemId')) || null,
    nameEN: text(pick(doc, 'nameEn')),
    nameTH: text(pick(doc, 'nameTh')),
    documentCategory: text(pick(doc, 'documentCategory')),
    title: text(pick(doc, 'title')),
    subTitle: text(pick(doc, 'subTitle')),
    docType: 'Center',
  };
}

/** graph ของ Center → รูป `ResponseMetaCommunityDropdown` (+documentCount/isActive) · ไม่มี id ⇒ null */
export function toGraphOption(graph) {
  const id = pick(graph, 'id');
  if (id === undefined || id === null || id === '') return null;
  const groupCount = pick(graph, 'groupCount');
  const documentCount = pick(graph, 'documentCount');
  return {
    id,
    communityName: text(pick(graph, 'communityName')) || String(id),
    description: text(pick(graph, 'description')),
    groupCount: groupCount === undefined ? null : groupCount,
    documentCount: documentCount === undefined || documentCount === null ? 0 : documentCount,
    isActive: pick(graph, 'isActive') !== false,
  };
}

function orderOf(row) {
  const v = pick(row, 'displayOrder');
  return typeof v === 'number' ? v : Number(v) || 0;
}

/** แถวทะเบียนหัวข้อ → `{ title, code, displayOrder, isActive }` · ไม่มี title ⇒ null */
export function toCategoryRow(row) {
  const title = text(pick(row, 'title'));
  if (!title) return null;
  return { title, code: text(pick(row, 'code')), displayOrder: orderOf(row), isActive: pick(row, 'isActive') !== false };
}

/** แถวทะเบียนหมวด → `{ categoryTitle, key, code, displayOrder, isActive }` · categoryTitle ว่างใช้หัวข้อที่ขอ */
export function toSubCategoryRow(row, headerTitle) {
  const key = text(pick(row, 'key'));
  const categoryTitle = text(pick(row, 'categoryTitle')) || text(headerTitle);
  if (!key || !categoryTitle) return null;
  return { categoryTitle, key, code: text(pick(row, 'code')), displayOrder: orderOf(row), isActive: pick(row, 'isActive') !== false };
}

/**
 * สร้างต้นไม้หัวข้อ ▸ หมวดจาก catalog (ทางสำรองเมื่ออ่านทะเบียนไม่ได้): หัวข้อ = `documentCategory`, หมวด = `title`
 * — คู่เดียวกับที่ runtime จับ (`ResolveByTagsAsync`) ⇒ ทุกตัวเลือกมีเอกสารจริงอย่างน้อย 1 ฉบับ · ไม่ซ้ำ (ไม่สนตัวพิมพ์)
 * @param {Array} rows แถว catalog
 * @returns {{headers: Array, categories: Array}}
 */
export function taxonomyFromCatalog(rows) {
  const headers = new Map();
  const categories = new Map();
  (Array.isArray(rows) ? rows : []).forEach((doc) => {
    const header = text(pick(doc, 'documentCategory'));
    if (!header) return;
    const hk = header.toLowerCase();
    if (!headers.has(hk)) headers.set(hk, { title: header, code: '', displayOrder: 0, isActive: true });
    const key = text(pick(doc, 'title'));
    if (!key) return;
    const ck = `${hk}\u0000${key.toLowerCase()}`;
    if (!categories.has(ck)) {
      categories.set(ck, { categoryTitle: headers.get(hk).title, key, code: '', displayOrder: 0, isActive: true });
    }
  });
  const byTitle = (a, b) => a.title.localeCompare(b.title);
  const byKey = (a, b) => a.categoryTitle.localeCompare(b.categoryTitle) || a.key.localeCompare(b.key);
  return { headers: [...headers.values()].sort(byTitle), categories: [...categories.values()].sort(byKey) };
}

/** error → ชื่อ i18n · 412 = ไม่มี key ที่ใช้ (เหตุผลของรายการนั้น) · ไม่มี response = ติดต่อไม่ได้ */
function reasonOf(error, notConfiguredReason) {
  const httpStatus = error && error.httpStatus;
  if (httpStatus === 412) return notConfiguredReason;
  if (httpStatus === undefined || httpStatus === null) return 'kccCenterUnreachable';
  return kccErrorKey(error);
}

function normalizeStatus(data) {
  if (!data || typeof data !== 'object') return OFF;
  const enabled = pick(data, 'enabled') === true;
  return Object.freeze({
    enabled,
    runtimeReady: enabled && pick(data, 'runtimeReady') === true,
    adminReady: enabled && pick(data, 'adminReady') === true,
  });
}

/**
 * @param {Object} [options]
 * @param {{status: Function, catalog: Function, graphs: Function}} [options.api] ค่าตั้งต้น `kccApi`
 * @param {{get: Function}} [options.http] axios instance สำหรับ passthrough ทะเบียน — ค่าตั้งต้น `kccHttp`
 * @param {() => number} [options.now] นาฬิกา (เทสต์ TTL)
 */
export function createKccPickerSource({ api = defaultApi, http = kccHttp, now = () => Date.now() } = {}) {
  let statusCache = null; // { at, promise }
  let catalogCache = null; // { at, promise } — เก็บเฉพาะครั้งที่สำเร็จ

  /** โหมด Center + ความพร้อม · พลาด/401 ⇒ ปิด · ไม่ throw */
  function status({ force = false } = {}) {
    if (!force && statusCache && now() - statusCache.at < STATUS_TTL_MS) return statusCache.promise;
    const promise = Promise.resolve()
      .then(() => api.status())
      .then(normalizeStatus, () => OFF);
    statusCache = { at: now(), promise };
    return promise;
  }

  async function isCenter() {
    return (await status()).enabled;
  }

  function loadCatalog() {
    if (catalogCache && now() - catalogCache.at < CATALOG_TTL_MS) return catalogCache.promise;
    const promise = Promise.resolve().then(() => api.catalog());
    const entry = { at: now(), promise };
    catalogCache = entry;
    promise.catch(() => {
      if (catalogCache === entry) catalogCache = null; // พลาดไม่ cache — ครั้งหน้าลองใหม่
    });
    return promise;
  }

  /** catalog ของโหมดที่เปิดแล้ว → `{ rows, reason }` (reason ≠ '' ⇒ rows ว่าง) */
  async function centerCatalog(s) {
    if (!s.runtimeReady) return { rows: [], reason: KCC_LIST_UNAVAILABLE };
    try {
      const data = await loadCatalog();
      return { rows: Array.isArray(data) ? data : [], reason: '' };
    } catch (e) {
      return { rows: [], reason: reasonOf(e, KCC_LIST_UNAVAILABLE) };
    }
  }

  async function getJson(url) {
    try {
      return unwrap(await http.get(url));
    } catch (e) {
      throw toError(e);
    }
  }

  /** null = โหมดปิด (ใช้ $call เดิม) · ไม่งั้นรายการเอกสาร Center รูป DDVector (อาจว่าง + เหตุผล) */
  async function vectorList() {
    const s = await status();
    if (!s.enabled) return null;
    const c = await centerCatalog(s);
    return annotate(c.rows.map(toVectorOption).filter(Boolean), { reason: c.reason, source: 'catalog' });
  }

  /** null = โหมดปิด · ไม่งั้นรูป `GetAvailableMetaDocuments` · `category`/`type` กรอง title/subTitle (ตรงตัว) แบบเส้นเดิม */
  async function availableMetaDocuments({ category = null, type = null } = {}) {
    const s = await status();
    if (!s.enabled) return null;
    const c = await centerCatalog(s);
    let list = c.rows.map(toAvailableMetaDocument).filter(Boolean);
    if (category) list = list.filter((d) => d.title === category);
    if (type) list = list.filter((d) => d.subTitle === type);
    return annotate(list, { reason: c.reason, source: 'catalog' });
  }

  /** null = โหมดปิด · ไม่งั้น graph ของ Center ที่เปิดใช้ (id ของ Center — D2) */
  async function graphs() {
    const s = await status();
    if (!s.enabled) return null;
    if (!s.runtimeReady) return annotate([], { reason: KCC_GRAPHS_UNAVAILABLE, source: 'graphs' });
    try {
      const data = await api.graphs();
      const list = (Array.isArray(data) ? data : []).map(toGraphOption).filter((g) => g && g.isActive);
      return annotate(list, { source: 'graphs' });
    } catch (e) {
      return annotate([], { reason: reasonOf(e, KCC_GRAPHS_UNAVAILABLE), source: 'graphs' });
    }
  }

  async function registryHeaders(s) {
    if (!s.adminReady) return null; // ไม่มี Admin key ⇒ proxy ตอบ 412 แน่ ๆ — ไม่ต้องยิง
    try {
      const data = await getJson(KCC_PICKER_PATHS.metaDocCategories);
      return (Array.isArray(data) ? data : []).map(toCategoryRow).filter(Boolean);
    } catch (e) {
      return null;
    }
  }

  async function registrySubCategories(s, headers) {
    if (!s.adminReady) return null;
    const titles = [];
    const seen = new Set();
    (Array.isArray(headers) ? headers : []).forEach((h) => {
      if (h && typeof h === 'object' && pick(h, 'isActive') === false) return; // หัวข้อที่ปิดไม่ต้องถามหมวด
      const title = text(typeof h === 'string' ? h : pick(h, 'title'));
      if (title && !seen.has(title)) { seen.add(title); titles.push(title); }
    });
    try {
      const lists = await Promise.all(
        titles.map((title) =>
          getJson(KCC_PICKER_PATHS.metaDocSubCategoriesByCategory.replace('{categoryTitle}', encodeURIComponent(title))))
      );
      const out = [];
      lists.forEach((data, i) => {
        (Array.isArray(data) ? data : []).forEach((row) => {
          const r = toSubCategoryRow(row, titles[i]);
          if (r) out.push(r);
        });
      });
      return out;
    } catch (e) {
      return null; // หัวข้อใดพลาด ⇒ ทั้งชุดใช้ catalog (ต้นไม้มาจากแหล่งเดียวเสมอ)
    }
  }

  /** null = โหมดปิด · ไม่งั้นหัวข้อของ Center (ทะเบียน › สำรองจาก catalog) */
  async function metaDocCategories() {
    const s = await status();
    if (!s.enabled) return null;
    const rows = await registryHeaders(s);
    if (rows !== null) return annotate(rows, { source: 'registry' });
    const c = await centerCatalog(s);
    return annotate(taxonomyFromCatalog(c.rows).headers, { reason: c.reason, source: 'catalog' });
  }

  /** null = โหมดปิด · ไม่งั้นหมวดของทุกหัวข้อใน `headers` (ทะเบียน › สำรองจาก catalog ทั้งชุด) */
  async function metaDocSubCategoriesAll(headers) {
    const s = await status();
    if (!s.enabled) return null;
    const rows = await registrySubCategories(s, headers);
    if (rows !== null) return annotate(rows, { source: 'registry' });
    const c = await centerCatalog(s);
    return annotate(taxonomyFromCatalog(c.rows).categories, { reason: c.reason, source: 'catalog' });
  }

  /**
   * null = โหมดปิด · ไม่งั้น `{ headers, categories, source, reason }` สำหรับต้นไม้หัวข้อ ▸ หมวด
   * (ทะเบียนครบทั้งสองชั้น ⇒ 'registry' · ชั้นใดพลาด ⇒ ทั้งต้นไม้จาก catalog 'catalog')
   */
  async function metaDocTaxonomy() {
    const s = await status();
    if (!s.enabled) return null;
    const headers = await registryHeaders(s);
    if (headers !== null) {
      const categories = await registrySubCategories(s, headers);
      if (categories !== null) return { headers, categories, source: 'registry', reason: '' };
    }
    const c = await centerCatalog(s);
    const t = taxonomyFromCatalog(c.rows);
    return { headers: t.headers, categories: t.categories, source: 'catalog', reason: c.reason };
  }

  /** ล้าง cache (status + catalog) — เช่นหลังบันทึกค่าตั้งของ Center ในหน้าเดียวกัน */
  function reset() {
    statusCache = null;
    catalogCache = null;
  }

  return Object.freeze({
    status,
    isCenter,
    vectorList,
    availableMetaDocuments,
    graphs,
    metaDocCategories,
    metaDocSubCategoriesAll,
    metaDocTaxonomy,
    reset,
  });
}

/** ตัวกลางของทั้งแอป (cache ร่วมกันทุกหน้า) */
export const kccPickerSource = createKccPickerSource();

export default kccPickerSource;
